/**
 * Bulk "Map shared stock" dialog (BLU-711 Part B Stage 2 PR3).
 *
 * Selection-scoped bulk action over `productSync.bulkMapSharedSkus`: every
 * SYNCED product in the selection has its blank codes matched against ONE
 * owner's nominated shared catalog by EXACT code. The backend reports every
 * ambiguity (no match, claimed by another org, conflict, over-sized group)
 * and resolves none of them — the per-product Shared stock tier is the
 * resolver — so the summary here must surface every outcome bucket, not a
 * bare success count.
 *
 * Conventions mirrored from `BulkMarkAsPapDialog`: over-cap is a refusal
 * STATE (never a silent truncation), the affected titles are listed before
 * confirm. Unlike that dialog this one owns its mutation: bulk-map is one
 * call, and its outcome summary is the dialog's second phase (a toast cannot
 * carry eight labelled counts).
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Link2, Loader2 } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { trpc } from '@/lib/trpc';

import type { SelectedProductLite } from '../lib/types';

/**
 * Mirrors the backend `BULK_MAP_BATCH_SIZE` (shared-stock.service.ts) — the
 * per-call election bound a single blank-code group must fit inside. Keep in
 * lockstep; it is interpolated into `bulkMap.outcome.groupTooLargeHint`.
 */
const BULK_MAP_GROUP_CAP = 200;

interface BulkMapSharedStockDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  products: SelectedProductLite[];
  /** Mirrors the backend `bulkMapSharedSkus` productIds zod cap. */
  maxPerRun: number;
}

/** Ordered (key, i18n suffix) pairs for the totals summary — render only non-zero rows. */
const OUTCOME_ROWS = [
  ['rowsMapped', 'outcome.rowsMapped'],
  ['rowsAlreadyYours', 'outcome.rowsAlreadyYours'],
  ['rowsUnmatched', 'outcome.rowsUnmatched'],
  ['rowsClaimedByOther', 'outcome.rowsClaimedByOther'],
  ['rowsGroupTooLarge', 'outcome.rowsGroupTooLarge'],
  ['rowsPendingSync', 'outcome.rowsPendingSync'],
  ['rowsElectedPromotionPending', 'outcome.rowsElectedPromotionPending'],
  ['rowsConflict', 'outcome.rowsConflict'],
] as const;

export function BulkMapSharedStockDialog({
  open,
  onOpenChange,
  products,
  maxPerRun,
}: BulkMapSharedStockDialogProps) {
  const { t } = useTranslation('productSync');
  const utils = trpc.useUtils();

  const overCap = products.length > maxPerRun;

  const ownersQuery = trpc.inventory.listSharedStockOwners.useQuery(undefined, {
    enabled: open,
    staleTime: 300_000,
  });
  const owners = ownersQuery.data?.owners ?? [];
  const [ownerChoice, setOwnerChoice] = useState<string | null>(null);
  const effectiveOwnerId = ownerChoice ?? (owners.length === 1 ? (owners[0]?.id ?? null) : null);

  const bulkMap = trpc.productSync.bulkMapSharedSkus.useMutation({
    onSuccess: () => {
      // The mapping landscape moved for every touched product.
      void utils.productSync.getProductSyncPreview.invalidate();
      void utils.productSync.getVariantSharedSkuLinks.invalidate();
    },
    onError: (error) => {
      toast.error(t('bulkMap.errorToast', { message: error.message }));
    },
  });

  // Fresh run per open: a stale summary re-shown for a NEW selection would
  // report the wrong products.
  useEffect(() => {
    if (open) {
      bulkMap.reset();
      setOwnerChoice(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bulkMap identity churns per render; reset-on-open only.
  }, [open]);

  const result = bulkMap.data ?? null;

  const handleConfirm = () => {
    if (!effectiveOwnerId || products.length === 0 || overCap) return;
    bulkMap.mutate({
      productIds: products.map((p) => p.id),
      ownerOrganizationId: effectiveOwnerId,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !bulkMap.isPending && onOpenChange(next)}>
      <DialogContent className="sm:max-w-md">
        {overCap ? (
          <>
            <DialogHeader>
              <DialogTitle>{t('bulkMap.tooManyTitle')}</DialogTitle>
              <DialogDescription>
                {t('bulkMap.tooManyBody', { count: products.length, max: maxPerRun })}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
                {t('bulkMap.tooManyClose')}
              </Button>
            </DialogFooter>
          </>
        ) : result ? (
          <>
            <DialogHeader>
              <DialogTitle>{t('bulkMap.summaryTitle')}</DialogTitle>
              <DialogDescription>{t('bulkMap.summaryUnit')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {result.syncStatusUnavailable && (
                <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800" role="alert">
                  {t('bulkMap.summarySyncUnavailable')}
                </p>
              )}
              <dl className="space-y-1">
                {OUTCOME_ROWS.map(([key, labelKey]) => {
                  const value = result.totals[key];
                  if (value === 0) return null;
                  return (
                    <div key={key} className="flex items-baseline justify-between gap-3 text-xs">
                      <dt className={key === 'rowsMapped' ? 'font-medium text-emerald-700' : 'text-muted-foreground'}>
                        {t(`bulkMap.${labelKey}`)}
                      </dt>
                      <dd className="font-semibold tabular-nums">{value}</dd>
                    </div>
                  );
                })}
                {result.totals.productsSkippedUnsynced > 0 && (
                  <div className="flex items-baseline justify-between gap-3 text-xs">
                    <dt className="text-muted-foreground">{t('bulkMap.productsSkippedUnsynced')}</dt>
                    <dd className="font-semibold tabular-nums">{result.totals.productsSkippedUnsynced}</dd>
                  </div>
                )}
                {result.totals.productsSyncStatusUnknown > 0 && (
                  <div className="flex items-baseline justify-between gap-3 text-xs">
                    <dt className="text-muted-foreground">{t('bulkMap.productsSyncStatusUnknown')}</dt>
                    <dd className="font-semibold tabular-nums">{result.totals.productsSyncStatusUnknown}</dd>
                  </div>
                )}
              </dl>
              {result.totals.rowsGroupTooLarge > 0 && (
                <p className="text-xs text-amber-800">
                  {t('bulkMap.outcome.groupTooLargeHint', { max: BULK_MAP_GROUP_CAP })}
                </p>
              )}
              {(result.totals.rowsUnmatched > 0 ||
                result.totals.rowsClaimedByOther > 0 ||
                result.totals.rowsConflict > 0) && (
                <p className="text-xs text-muted-foreground">{t('bulkMap.resolveHint')}</p>
              )}
            </div>
            <DialogFooter>
              <Button size="sm" onClick={() => onOpenChange(false)} className="h-8 px-3 text-xs">
                {t('bulkMap.done')}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{t('bulkMap.dialogTitle', { count: products.length })}</DialogTitle>
              <DialogDescription>{t('bulkMap.dialogBody')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {owners.length === 0 && !ownersQuery.isLoading ? (
                <p className="text-xs text-muted-foreground">{t('sharedStock.noOwners')}</p>
              ) : owners.length > 1 ? (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="bulk-map-owner" className="text-sm">
                    {t('sharedStock.ownerLabel')}
                    <span aria-hidden="true" className="text-destructive ml-0.5">*</span>
                  </Label>
                  <Select value={effectiveOwnerId ?? undefined} onValueChange={setOwnerChoice}>
                    <SelectTrigger id="bulk-map-owner" className="h-8 text-xs">
                      <SelectValue placeholder={t('sharedStock.ownerPlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {owners.map((owner) => (
                        <SelectItem key={owner.id} value={owner.id}>
                          <span className="flex items-center gap-2">
                            <Link2 className="h-3.5 w-3.5 shrink-0 text-threadheads-purple" aria-hidden="true" />
                            {owner.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : owners.length === 1 ? (
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Link2 className="h-3.5 w-3.5 shrink-0 text-threadheads-purple" aria-hidden="true" />
                  {t('bulkMap.singleOwner', { name: owners[0]?.name ?? '' })}
                </p>
              ) : null}
              <ul className="max-h-52 divide-y overflow-y-auto rounded-md border">
                {products.map((p) => (
                  <li key={p.id} className="truncate px-3 py-1.5 text-xs">{p.title}</li>
                ))}
              </ul>
            </div>
            <DialogFooter>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-3 text-xs"
                disabled={bulkMap.isPending}
                onClick={() => onOpenChange(false)}
              >
                {t('sharedStock.cancel')}
              </Button>
              <Button
                size="sm"
                className="h-8 gap-1.5 bg-threadheads-purple px-3 text-xs text-white hover:bg-threadheads-purple/90"
                disabled={!effectiveOwnerId || products.length === 0 || bulkMap.isPending}
                onClick={handleConfirm}
              >
                {bulkMap.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                ) : (
                  <Link2 className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                {bulkMap.isPending
                  ? t('bulkMap.running')
                  : t('bulkMap.confirm', { count: products.length })}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
